import React, { useState, useEffect, ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';

interface ImageViewerProps {
    imagePath?: string;
    refreshKey?: string | number;
    placeholderComponent?: ReactNode;
    styleType?: 'portrait' | 'landscape' | 'circle';
    altText?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ imagePath, refreshKey, placeholderComponent, styleType = 'portrait', altText = 'Service Image' }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isFetchingImageUrl, setIsFetchingImageUrl] = useState<boolean>(false);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

    const getBoxStyle = () => {
        switch (styleType) {
            case 'portrait':
                return {
                    width: "100%",
                    maxHeight: "500px",
                    aspectRatio: "3 / 4",
                };
            case 'landscape':
                return {
                    width: "100%",
                    maxHeight: "300px",
                    aspectRatio: "4 / 3",
                };
            case 'circle':
                return {
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    overflow: "hidden",
                };
            default:
                return {};
        }
    };
    const boxStyle = getBoxStyle();

    useEffect(() => {
        let isCancelled = false;

        if (imagePath) {
            const fetchImageUrl = async () => {
                setIsFetchingImageUrl(true);
                setIsImageLoaded(false);
                setImageUrl(null);
                try {
                    const downloadUrl = await getDownloadURL(ref(storage, imagePath));
                    const fullImagePath = refreshKey === undefined
                        ? downloadUrl
                        : `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}refresh=${refreshKey}`;
                    if (!isCancelled) {
                        setImageUrl(fullImagePath);
                    }
                } catch (error) {
                    if (!isCancelled) {
                        setImageUrl(null);
                        setIsImageLoaded(false);
                    }
                } finally {
                    if (!isCancelled) {
                        setIsFetchingImageUrl(false);
                    }
                }
            };

            fetchImageUrl();
        } else {
            setImageUrl(null);
            setIsImageLoaded(false);
            setIsFetchingImageUrl(false);
        }

        return () => {
            isCancelled = true;
        };
    }, [imagePath, refreshKey]);

    const isLoading = isFetchingImageUrl || (Boolean(imageUrl) && !isImageLoaded);

    return (
        <Box
            sx={{
                ...boxStyle,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: 'relative',
            }}
        >
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={altText}
                    onLoad={() => setIsImageLoaded(true)}
                    onError={() => {
                        setImageUrl(null);
                        setIsImageLoaded(false);
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        opacity: isImageLoaded ? 1 : 0,
                        transition: 'opacity 160ms ease',
                    }}
                />
            ) : (
                placeholderComponent || null
            )}
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={36} />
                </Box>
            )}
        </Box>
    );
};

export default ImageViewer;
